/**
 * GhostFart - Self-moving fart that seeks players and phases through walls
 * 
 * Behavior:
 * 1. Wanders randomly for first 2 seconds (owner immunity period)
 * 2. After 2s, seeks nearest player within detection radius
 * 3. Phases through walls (no collision with maze)
 * 4. High damage (25) with long stun (1s)
 * 5. Destroys on impact with any player (including owner after 2s)
 */

import Phaser from 'phaser';
import { FartType, FART_CONFIGS, GHOST_CONFIG } from './FartTypes';
import { IFart } from '../../interfaces/GameInterfaces';

export interface GhostFartConfig {
    ownerId: string;
}

enum GhostState {
    WANDERING = 'wandering',
    SEEKING = 'seeking',
    FADING = 'fading'
}

export class GhostFart extends Phaser.Physics.Arcade.Sprite implements IFart {
    public readonly fartType: FartType = FartType.GHOST;

    private ownerId: string;
    private ghostState: GhostState = GhostState.WANDERING;
    private spawnTime: number;
    private wanderAngle: number = 0;
    private wanderChangeTimer: number = 0;
    private targetPlayer: Phaser.GameObjects.Sprite | null = null;
    private players: Phaser.GameObjects.Sprite[] = [];

    // Config values
    private readonly SPEED = GHOST_CONFIG.speed;
    private readonly DETECTION_RADIUS = GHOST_CONFIG.detectionRadius;
    private readonly OWNER_IGNORE_DURATION = GHOST_CONFIG.ownerIgnoreDuration;
    private readonly DURATION = FART_CONFIGS[FartType.GHOST].duration;
    private readonly DAMAGE = FART_CONFIGS[FartType.GHOST].damage;
    private readonly STUN_DURATION = FART_CONFIGS[FartType.GHOST].stunDuration;

    constructor(scene: Phaser.Scene, x: number, y: number, config: GhostFartConfig) {
        super(scene, x, y, 'fart_ghost');

        this.ownerId = config.ownerId;
        this.spawnTime = scene.time.now;

        // Add to scene and physics
        scene.add.existing(this);
        scene.physics.add.existing(this);

        // Configure physics - no world collision, phases through everything
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setCircle(16);
        body.setOffset(8, 8);
        body.setCollideWorldBounds(false);

        // Visual setup
        this.setDepth(45);
        this.setScale(1.2);
        this.setAlpha(0.8);

        // Store owner data
        this.setData('ownerId', this.ownerId);
        this.setData('isGhost', true);

        // Initial random wander direction
        this.wanderAngle = Math.random() * Math.PI * 2;

        // Spawn animation - fade in
        this.setAlpha(0);
        scene.tweens.add({
            targets: this,
            alpha: 0.8,
            duration: 300,
            ease: 'Power2'
        });

        // Ghostly floating animation
        scene.tweens.add({
            targets: this,
            y: y - 6,
            duration: 1000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        // Subtle pulsing
        scene.tweens.add({
            targets: this,
            scaleX: 1.3,
            scaleY: 1.3,
            alpha: 0.9,
            duration: 800,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        // Set lifetime timer
        scene.time.delayedCall(this.DURATION - 1000, () => {
            this.startFading();
        });

        scene.time.delayedCall(this.DURATION, () => {
            this.destroy();
        });
    }

    /**
     * Set the list of players to track for seeking
     */
    setPlayers(players: Phaser.GameObjects.Sprite[]): void {
        this.players = players;
    }

    /**
     * Update is called by the system
     */
    update(time: number, delta: number): void {
        if (!this.active || this.ghostState === GhostState.FADING) return;

        const age = time - this.spawnTime;

        // Check if we should transition from wandering to seeking
        if (this.ghostState === GhostState.WANDERING && age > this.OWNER_IGNORE_DURATION) {
            this.ghostState = GhostState.SEEKING;
        }

        if (this.ghostState === GhostState.WANDERING) {
            this.updateWander(delta);
        } else if (this.ghostState === GhostState.SEEKING) {
            this.updateSeek(delta);
        }
    }

    /**
     * Random wandering movement
     */
    private updateWander(delta: number): void {
        this.wanderChangeTimer += delta;

        // Change direction randomly every 500-1000ms
        if (this.wanderChangeTimer > 500 + Math.random() * 500) {
            this.wanderChangeTimer = 0;
            // Slight random turn
            this.wanderAngle += (Math.random() - 0.5) * Math.PI * 0.5;
        }

        // Move in wander direction
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setVelocity(
            Math.cos(this.wanderAngle) * this.SPEED * 0.5,
            Math.sin(this.wanderAngle) * this.SPEED * 0.5
        );

        // Rotate sprite to face movement direction
        this.setRotation(this.wanderAngle + Math.PI / 2);
    }

    /**
     * Seek nearest valid target
     */
    private updateSeek(delta: number): void {
        // Find nearest player within detection radius
        this.targetPlayer = this.findNearestTarget();

        const body = this.body as Phaser.Physics.Arcade.Body;

        if (this.targetPlayer) {
            // Move towards target
            const angle = Phaser.Math.Angle.Between(
                this.x, this.y,
                this.targetPlayer.x, this.targetPlayer.y
            );

            body.setVelocity(
                Math.cos(angle) * this.SPEED,
                Math.sin(angle) * this.SPEED
            );

            // Face the target
            this.setRotation(angle + Math.PI / 2);
        } else {
            // No target - continue wandering
            this.updateWander(delta);
        }
    }

    /**
     * Find the nearest valid target player
     */
    private findNearestTarget(): Phaser.GameObjects.Sprite | null {
        let nearest: Phaser.GameObjects.Sprite | null = null;
        let nearestDist = this.DETECTION_RADIUS;

        for (const player of this.players) {
            if (!player.active) continue;

            // Check if player is dead (health <= 0)
            const health = (player as any).health;
            if (typeof health === 'number' && health <= 0) continue;

            const dist = Phaser.Math.Distance.Between(
                this.x, this.y,
                player.x, player.y
            );

            if (dist < nearestDist) {
                nearestDist = dist;
                nearest = player;
            }
        }

        return nearest;
    }

    /**
     * Check if this ghost can damage a specific player
     */
    canDamagePlayer(playerId: string): boolean {
        const age = this.scene.time.now - this.spawnTime;

        // During owner ignore period, can't hit owner
        if (playerId === this.ownerId && age < this.OWNER_IGNORE_DURATION) {
            return false;
        }

        return true;
    }

    /**
     * Start fading out before destruction
     */
    private startFading(): void {
        this.ghostState = GhostState.FADING;

        // Stop movement
        const body = this.body as Phaser.Physics.Arcade.Body;
        body.setVelocity(0, 0);

        // Fade out
        this.scene.tweens.add({
            targets: this,
            alpha: 0,
            scaleX: 0.5,
            scaleY: 0.5,
            duration: 1000,
            ease: 'Power2'
        });
    }

    /**
     * Get the owner ID
     */
    getOwnerId(): string {
        return this.ownerId;
    }

    /**
     * IFart interface - get damage value
     */
    getDamage(): number {
        return this.DAMAGE;
    }

    /**
     * IFart interface - get stun duration
     */
    getStunDuration(): number {
        return this.STUN_DURATION;
    }

    /**
     * Override destroy to clean up
     */
    destroy(fromScene?: boolean): void {
        super.destroy(fromScene);
    }
}
